import {
  DEFAULT_DATE_FORMAT_FNS,
  EvaluationType,
  FilterKeys,
  GetAssessmentsQueryFilters,
} from '@schooly/api';
import { AssessmentStatuses } from '@schooly/constants';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UseAssessmentsFiltersProps = {
  filters: GetAssessmentsQueryFilters;
  onSetFilters: (filters: GetAssessmentsQueryFilters) => void;
};

export const useAssessmentsFilters = ({ filters, onSetFilters }: UseAssessmentsFiltersProps) => {
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetAssessmentStatus = (v: AssessmentStatuses[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.AssessmentStatus]: v,
      });
    };

    const onSetFiltersForKey = (key: keyof typeof filters) => (v: string[]) => {
      onSetFilters({
        ...filters,
        [key]: v,
      });
    };

    const onSetEvaluationType = (v: EvaluationType[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.EvaluationType]: v,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetAssessmentStatus,
      onClearAssessmentStatus: clearFilter(FilterKeys.AssessmentStatus),
      onSetGroup: onSetFiltersForKey(FilterKeys.Group),
      onClearGroup: clearFilter(FilterKeys.Group),
      onSetReport: onSetFiltersForKey(FilterKeys.Report),
      onClearReport: clearFilter(FilterKeys.Report),
      onSetSubject: onSetFiltersForKey(FilterKeys.Subject),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onSetStaff: onSetFiltersForKey(FilterKeys.Staff),
      onClearStaff: clearFilter(FilterKeys.Staff),
      onSetRecurrenceId: onSetFiltersForKey(FilterKeys.RecurrenceId),
      onClearRecurrenceId: clearFilter(FilterKeys.RecurrenceId),
      onSetEvaluationType,
      onClearEvaluationType: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.EvaluationType]: [],
          [FilterKeys.AreaOfLearning]: [],
        });
      },
      onSetAreaOfLearning: onSetFiltersForKey(FilterKeys.AreaOfLearning),
      onClearAreaOfLearning: clearFilter(FilterKeys.AreaOfLearning),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersAssessmentStatus = filters.assessment_status;
  const filtersGroup = filters.group;
  const filtersReport = filters.report;
  const filtersSubject = filters.subject;
  const filtersStaff = filters.staff;
  const filtersRecurrenceId = filters.recurrence_id;
  const filtersEvaluationType = filters.evaluation_type;
  const filtersAreaOfLearning = filters.area_of_learning;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.AssessmentStatus]: formatMessage({ id: 'assessments-Status' }),
    [FilterKeys.Group]: formatMessage({ id: 'asssessments-Group' }),
    [FilterKeys.Report]: formatMessage({ id: 'assessments-Report' }),
    [FilterKeys.Subject]: formatMessage({ id: 'groups-GroupSubject' }),
    [FilterKeys.Staff]: formatMessage({ id: 'schoolProperty-Staff' }),
    [FilterKeys.RecurrenceId]: formatMessage({ id: 'assessments-recurring' }),
    [FilterKeys.EvaluationType]: formatMessage({ id: 'assessments-AoLTutorsFeedback' }),
    [FilterKeys.AreaOfLearning]: formatMessage({ id: 'assessments-AoLTutorsFeedback' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  return {
    ...actions,
    labels,
    filtersDate,
    filtersAssessmentStatus,
    filtersGroup,
    filtersReport,
    filtersSubject,
    filtersStaff,
    filtersRecurrenceId,
    filtersEvaluationType,
    filtersAreaOfLearning,
  };
};
